/**
 * Watch helpers for `optimize start --watch` and `optimize watch <job-id>`.
 *
 * Extracted from optimize.ts to respect the 500-line file limit.
 */

import chalk from 'chalk';
import { createWatchClient, type StageResultPayload, type ScorecardUpdatePayload } from '../../lib/watch-client.js';
import { renderStageCard } from '../../lib/stage-cards.js';
import { getSDKClient } from '../../lib/sdk-client.js';
import { getApiKey, loadConfig } from '../../lib/config.js';
import { handleError, MutagentError } from '../../lib/errors.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { optimizerLink } from '../../lib/ui-links.js';
import {
  renderScorecard,
  renderOptimizationStatusCard,
} from '../../lib/scorecard.js';
import type { Command } from 'commander';

// =============================================================================
// Iteration Boundary Card
// =============================================================================

const BOX_WIDTH = 66;

function pad(text: string, width: number): string {
  // eslint-disable-next-line no-control-regex
  const stripped = text.replace(/\u001B\[[0-9;]*m/g, '');
  const remaining = width - stripped.length;
  return remaining > 0 ? text + ' '.repeat(remaining) : text;
}

function line(text: string): string {
  return `\u2502 ${pad(text, BOX_WIDTH - 2)} \u2502`;
}

export function renderIterationBoundaryCard(opts: {
  iteration: number;
  maxIterations: number;
  // #956: allow null/undefined \u2014 API can return null for queued/early-stage jobs
  bestScore: number | null | undefined;
  baselineScore?: number | null;
}): string {
  const { iteration, maxIterations, baselineScore } = opts;
  // Normalize null \u2192 undefined so downstream arithmetic is safe
  const bestScore = opts.bestScore ?? undefined;
  const delta =
    baselineScore != null && bestScore !== undefined ? bestScore - baselineScore : 0;
  const deltaStr = delta >= 0 ? `+${delta.toFixed(2)}` : delta.toFixed(2);
  const improving = delta > 0.001;
  const statusStr = improving ? 'Improving' : 'Converging';

  const title = ` \uD83D\uDD04 Iteration ${String(iteration)} of ${String(maxIterations)} `;
  const remaining = BOX_WIDTH - title.length - 1;
  const topBorder = `\u250C\u2500${title}${'\u2500'.repeat(Math.max(0, remaining))}\u2510`;
  const bottomBorder = `\u2514${'\u2500'.repeat(BOX_WIDTH)}\u2518`;

  const bestScoreStr = bestScore !== undefined ? bestScore.toFixed(2) : 'N/A';
  const lines: string[] = [];
  lines.push(topBorder);
  lines.push(line(`Best Score: ${bestScoreStr} (${deltaStr} from baseline)`));
  lines.push(line(`Status: ${statusStr}`));
  lines.push(bottomBorder);

  return lines.join('\n');
}

// =============================================================================
// Watch Client Config Builder
// =============================================================================

interface WatchClientConfig {
  baseUrl: string;
  workspaceId: string;
  token: string;
}

function getWatchClientConfig(): WatchClientConfig {
  const config = loadConfig();
  const apiKey = getApiKey();
  if (!apiKey) {
    throw new MutagentError(
      'AUTH_REQUIRED',
      'API key is required for watch mode',
      'Run: mutagent auth login',
    );
  }

  const baseUrl = config.endpoint ?? 'https://api.mutagent.io';
  const workspaceId = config.defaultWorkspace ?? '';

  if (!workspaceId) {
    throw new MutagentError(
      'WORKSPACE_REQUIRED',
      'A workspace must be configured for watch mode',
      'Run: mutagent config set defaultWorkspace <workspace-id>',
    );
  }

  return { baseUrl, workspaceId, token: apiKey };
}

// =============================================================================
// Watch: Live Streaming via WebSocket
// =============================================================================

export async function startWatchStream(
  jobId: string,
  isJson: boolean,
  maxIterations?: number,
  baselineScore?: number,
): Promise<void> {
  const { baseUrl, workspaceId, token } = getWatchClientConfig();

  return new Promise<void>((resolve, reject) => {
    const client = createWatchClient({
      baseUrl,
      jobId,
      workspaceId,
      token,
      onStageComplete: (stage: string, _iteration: number, data: StageResultPayload) => {
        if (isJson) {
          console.log(JSON.stringify({ type: 'stage', stage, data }));
        } else {
          const card = renderStageCard(stage, data);
          console.log(card);
          console.log('');
        }
      },
      onIterationComplete: (iteration: number, bestScore: number) => {
        if (isJson) {
          console.log(JSON.stringify({ type: 'iteration', iteration, bestScore }));
        } else {
          const card = renderIterationBoundaryCard({
            iteration,
            maxIterations: maxIterations ?? iteration,
            bestScore,
            baselineScore,
          });
          console.log(card);
          console.log('');
        }
      },
      onJobComplete: (finalScore: number | null | undefined) => {
        // #956: finalScore can be null from the server before scores are committed
        const scoreStr = finalScore != null ? finalScore.toFixed(2) : 'N/A';
        if (isJson) {
          console.log(JSON.stringify({ type: 'job_complete', finalScore: finalScore ?? null }));
        } else {
          console.log(chalk.green(`\u2713 Optimization complete! Final score: ${scoreStr}`));
          console.log(chalk.dim(`  View results: mutagent prompts optimize results ${jobId}`));
        }
        resolve();
      },
      onError: (error: Error) => {
        if (isJson) {
          console.log(JSON.stringify({ type: 'error', error: error.message }));
        } else {
          console.error(chalk.red(`\u2717 Watch error: ${error.message}`));
        }
        reject(error);
      },
      onScorecardUpdate: (payload: ScorecardUpdatePayload) => {
        if (payload.error) {
          // Sentinel: scorecard build/render failed on the server side
          if (isJson) {
            console.log(JSON.stringify({
              type: 'scorecard.update',
              iteration: payload.iteration,
              error: payload.error,
            }));
          } else {
            console.warn(chalk.yellow(`\u26a0 Scorecard unavailable for iteration ${String(payload.iteration)} (${payload.error.type})`));
          }
          return;
        }

        if (isJson) {
          // T-05 JSON mode: one NDJSON line per scorecard:update event
          console.log(JSON.stringify({
            type: 'scorecard.update',
            iteration: payload.iteration,
            scorecard: payload.scorecard,
          }));
        } else {
          // T-05 human mode: print rendered scorecard followed by blank line
          console.log(payload.renderedScorecard);
          console.log('');
        }
      },
    });

    void client.connect().catch(reject);
  });
}

// =============================================================================
// Watch: Completed Job Fallback (REST)
// =============================================================================

async function renderCompletedJob(jobId: string, isJson: boolean): Promise<void> {
  const client = await getSDKClient();
  const results = await client.getOptimizationResults(jobId);
  const resultData = results as Record<string, unknown>;

  if (isJson) {
    const output = new OutputFormatter('json');
    const jobObj = resultData.job as Record<string, unknown> | undefined;
    const pId = (jobObj?.promptId as string | number | undefined) ?? jobId;
    output.output({
      ...resultData,
      _links: { optimizer: optimizerLink(pId, jobId) },
    });
  } else {
    renderScorecard(resultData as unknown as Parameters<typeof renderScorecard>[0]);
  }
}

// =============================================================================
// Watch Subcommand Action
// =============================================================================

export async function watchAction(
  jobId: string,
  options: { json?: boolean },
  parentCommand: Command,
): Promise<void> {
  const isJson = options.json ?? getJsonFlag(parentCommand);
  const output = new OutputFormatter(isJson ? 'json' : 'table');

  try {
    const client = await getSDKClient();
    const status = await client.getOptimizationStatus(jobId);

    switch (status.status) {
      case 'completed':
        if (!isJson) {
          output.info('Job already completed. Rendering results...');
          console.log('');
        }
        await renderCompletedJob(jobId, isJson);
        break;

      case 'running':
      case 'queued':
        if (!isJson) {
          renderOptimizationStatusCard(status);
          console.log(chalk.dim('Watching for live updates...\n'));
        }
        await startWatchStream(
          jobId,
          isJson,
          status.maxIterations,
          // #956: bestScore can be null from API on queued/early-stage jobs; normalize to undefined
          status.bestScore ?? undefined,
        );
        break;

      case 'failed': {
        // #956: surface failureMode + error from the raw API response.
        // The OptimizeStatus type doesn't declare these yet; cast through unknown.
        const rawStatus = status as unknown as Record<string, unknown>;
        const failureMode = rawStatus.failureMode as string | undefined;
        const jobError = rawStatus.error as string | undefined;
        if (isJson) {
          output.output({
            success: false,
            error: jobError ?? 'Optimization job failed',
            status: status.status,
            jobId,
            ...(failureMode ? { failureMode } : {}),
            message: status.message,
          });
        } else {
          console.error(chalk.red(`\u2717 Optimization job ${jobId} failed.`));
          if (failureMode) {
            console.error(chalk.dim(`  Failure mode: ${failureMode}`));
          }
          if (jobError) {
            console.error(chalk.dim(`  ${jobError}`));
          } else if (status.message) {
            console.error(chalk.dim(`  ${status.message}`));
          }
        }
        process.exitCode = 1;
        break;
      }

      case 'cancelled':
        if (isJson) {
          output.output({
            success: false,
            error: 'Optimization job was cancelled',
            status: status.status,
            jobId,
          });
        } else {
          console.error(chalk.yellow(`Optimization job ${jobId} was cancelled.`));
        }
        break;

      default:
        if (isJson) {
          output.output({
            success: false,
            error: `Unexpected job status: ${status.status}`,
            status: status.status,
            jobId,
          });
        } else {
          console.error(chalk.yellow(`Unexpected job status: ${status.status}`));
        }
        break;
    }
  } catch (error) {
    handleError(error, isJson);
  }
}
